import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { toArucoPicture } from '@modules/aruco/adapters/aruco-picture.adapter';
import { ArucoSticker } from '@modules/aruco/models/aruco-sticker';
import { ArucoDetectionService } from '@modules/aruco/services/aruco-detection.service';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Media } from '@pixacare/domain';
import { DialogService } from '@services/dialog.service';
import { TuiDialogContext, TuiNotificationService } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { catchError, EMPTY, first, iif, map, Observable, of, switchMap } from 'rxjs';
import { WoundtrackQuickService } from '../../../woundtrack-quick/services/woundtrack-quick.service';
import {
  WoundtrackQuickInputsModuleComponentInputs,
  WoundtrackQuickInputsModuleComponentOutputs,
} from '../../models/woundtrack-quick-inputs-module-component';

@Component({
  selector: 'pxc-woundtrack-quick-inputs',
  imports: [LoaderComponent, TranslocoDirective],
  templateUrl: './woundtrack-quick-inputs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full w-full py-6',
  },
  providers: [WoundtrackQuickService, ArucoDetectionService],
})
export class WoundtrackQuickInputsComponent implements OnInit {
  private readonly context =
    inject<TuiDialogContext<WoundtrackQuickInputsModuleComponentOutputs, WoundtrackQuickInputsModuleComponentInputs>>(
      POLYMORPHEUS_CONTEXT,
    );

  private readonly arucoDetectionService = inject(ArucoDetectionService);
  private readonly dialogService = inject(DialogService);
  private readonly alertService = inject(TuiNotificationService);
  private readonly translocoService = inject(TranslocoService);
  private readonly woundtrackQuickService = inject(WoundtrackQuickService);

  private medias: WoundtrackQuickInputsModuleComponentInputs['medias'] = this.context.data?.medias;

  get media(): Media {
    const [media] = this.medias;
    if (!media) {
      console.error('Media is required');
      this.context.completeWith(null);
      return null;
    }
    return media;
  }

  private readonly sequenceAlreadyHasAnalysis$: Observable<boolean> = this.woundtrackQuickService
    .selectSequence$(this.media)
    .pipe(map((sequence) => !!sequence?.sequenceInstance?.formAnswerId));

  ngOnInit(): void {
    this.canContinue$()
      .pipe(
        switchMap((canContinue) => {
          if (!canContinue) {
            this.context.completeWith(null);
            return EMPTY;
          }
          return this.arucoDetectionService.getArucoSticker$(this.media.uri).pipe(
            catchError((error) => {
              console.error('ArUco detection failed:', error);
              this.alertService
                .open(this.translocoService.translate('Échec de la détection du sticker ArUco'), {
                  appearance: 'negative',
                })
                .subscribe();
              this.context.completeWith(null);
              return EMPTY;
            }),
          );
        }),
        map((arucoSticker: ArucoSticker) => toArucoPicture(this.media, arucoSticker)),
      )
      .subscribe((arucoPicture) => {
        this.woundtrackQuickService.loadPatientSadmEntities(this.media);
        this.context.completeWith({
          picture: arucoPicture,
          isNewPicture: false,
        });
      });
  }

  canContinue$(): Observable<boolean> {
    return this.sequenceAlreadyHasAnalysis$.pipe(
      first(),
      switchMap((sequenceAlreadyHasAnalysis) =>
        iif(() => sequenceAlreadyHasAnalysis, this.openConfirmDialog(), of(true)),
      ),
    );
  }

  openConfirmDialog(): Observable<boolean> {
    return this.dialogService.openConfirm(this.translocoService.translate("Écraser l'analyse existante ?"), {
      content: this.translocoService.translate(
        // eslint-disable-next-line max-len
        "Une analyse est déjà renseignée sur cette séquence. Cette action remplacera l'analyse existante. Voulez-vous continuer ?",
      ),
    });
  }
}
